/**
 * Cache Presets for React Query
 * 
 * Standardized caching configurations for different data categories.
 * Use these presets in hooks to ensure consistent caching behavior across the app.
 * 
 * ## Usage
 * 
 * ```typescript
 * import { cachePresets } from '~/lib/query/cachePresets';
 * 
 * export function useBayAvailabilityQuery() {
 *   return useQuery({
 *     queryKey: [...],
 *     queryFn: () => {...},
 *     ...cachePresets.realTime,
 *   });
 * }
 * ```
 * 
 * ## Preset Categories
 * 
 * | Preset | staleTime | gcTime | Use Case |
 * |--------|-----------|--------|----------|
 * | realTime | 0 | 30s | Availability, live status |
 * | frequent | 1 min | 5 min | Validation, active sessions |
 * | stable | 5 min | 10 min | User profile, club details |
 * | staticData | 15 min | 30 min | Payment settings, terms |
 * 
 * ## Key Concepts
 * 
 * - **staleTime**: How long data is considered "fresh". Fresh data won't trigger refetch.
 * - **gcTime**: How long inactive data stays in cache before garbage collection.
 * - **refetchOnWindowFocus**: Whether to refetch when user returns to the browser tab.
 * 
 * ## Decision Guide
 * 
 * Ask yourself: "How quickly could this data become invalid?"
 * 
 * - **Seconds** → `realTime` (availability can change any moment)
 * - **Minutes** → `frequent` (validation might change, but user is actively working)
 * - **Several minutes** → `stable` (user profile, club info rarely changes mid-session)
 * - **Hours** → `staticData` (terms, payment config almost never changes)
 * 
 * @module query/cachePresets
 */

/**
 * Real-time data preset
 * 
 * For data that can change at any moment and must always be fresh.
 * 
 * **Use for:**
 * - Bay/slot availability
 * - Club open/closed status
 * - Live booking counts
 * 
 * **Behavior:**
 * - Data is immediately considered stale after fetch
 * - Refetches when user returns to tab (catches changes while away)
 * - Short cache lifetime (30s) to avoid memory bloat
 */
export const realTime = {
  staleTime: 0,
  gcTime: 30 * 1000, // 30 seconds
  refetchOnWindowFocus: true,
} as const;

/**
 * Frequent updates preset
 * 
 * For data that changes relatively often but doesn't need to be real-time.
 * 
 * **Use for:**
 * - Booking validation (checking limits/conflicts)
 * - Active user sessions
 * - Recent activity lists
 * 
 * **Behavior:**
 * - Data stays fresh for 1 minute
 * - Refetches when user returns to tab
 * - Moderate cache lifetime (5 min)
 */
export const frequent = {
  staleTime: 60 * 1000, // 1 minute
  gcTime: 5 * 60 * 1000, // 5 minutes
  refetchOnWindowFocus: true,
} as const;

/**
 * Stable data preset
 * 
 * For data that rarely changes during a user session.
 * 
 * **Use for:**
 * - User profile
 * - Club details (name, address, timezone)
 * - Payment methods list
 * - Booking details (after creation)
 * 
 * **Behavior:**
 * - Data stays fresh for 5 minutes
 * - No automatic refetch on window focus (reduces unnecessary requests)
 * - Longer cache lifetime (10 min)
 */
export const stable = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  gcTime: 10 * 60 * 1000, // 10 minutes
  refetchOnWindowFocus: false,
} as const;

/**
 * Static data preset
 * 
 * For data that almost never changes.
 * 
 * **Use for:**
 * - Payment settings (Stripe keys)
 * - Terms and conditions
 * - Feature flags
 * - Club configuration
 * 
 * **Behavior:**
 * - Data stays fresh for 15 minutes
 * - No automatic refetch on window focus
 * - Long cache lifetime (30 min)
 */
export const staticData = {
  staleTime: 15 * 60 * 1000, // 15 minutes
  gcTime: 30 * 60 * 1000, // 30 minutes
  refetchOnWindowFocus: false,
} as const;

/**
 * All cache presets as a single object
 * 
 * @example
 * ```typescript
 * import { cachePresets } from '~/lib/query/cachePresets';
 * 
 * // Spread into useQuery options
 * useQuery({
 *   queryKey: ['availability'],
 *   queryFn: fetchAvailability,
 *   ...cachePresets.realTime,
 * });
 * ```
 */
export const cachePresets = {
  realTime,
  frequent,
  stable,
  staticData,
} as const;

export type CachePreset = typeof realTime | typeof frequent | typeof stable | typeof staticData;
